/**
 * Bridge between the backend's row-array wire format and the shape each
 * viz library expects. Ported from cognos-plugin with import paths
 * adjusted for operate-ui.
 */
import type { VizRenderPayload } from "./types";

export function rowsAsObjects(
  rows: Array<Array<unknown>>,
  columnNames: string[],
): Array<Record<string, unknown>> {
  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    for (let i = 0; i < columnNames.length; i++) {
      obj[columnNames[i]] = row[i];
    }
    return obj;
  });
}

export function rowsAsArrayOfArraysWithHeader(
  rows: Array<Array<unknown>>,
  columnNames: string[],
): Array<Array<unknown>> {
  return [columnNames, ...rows];
}

export function prepareVegaLiteSpec(payload: VizRenderPayload): Record<string, unknown> {
  const spec = JSON.parse(payload.specJson) as Record<string, unknown>;
  const objectRows = rowsAsObjects(payload.rows, payload.columnNames);
  spec.data = { values: objectRows };
  spec.$schema = "https://vega.github.io/schema/vega-lite/v5.json";
  spec.width = "container";
  spec.height = "container";
  spec.autosize = { type: "fit", contains: "padding", resize: true };
  return spec;
}

export function prepareEChartsOption(payload: VizRenderPayload): Record<string, unknown> {
  const option = JSON.parse(payload.specJson) as Record<string, unknown>;
  const dataset = option.dataset as Record<string, unknown> | undefined;
  if (dataset && dataset.source === "DATA_PLACEHOLDER") {
    dataset.source = rowsAsArrayOfArraysWithHeader(payload.rows, payload.columnNames);
  } else if (!dataset) {
    option.dataset = { source: rowsAsArrayOfArraysWithHeader(payload.rows, payload.columnNames) };
  }
  return option;
}

/**
 * Parse + transform a deck.gl spec:
 *
 * <ol>
 *   <li>Substitute {@code data: "DATA_PLACEHOLDER"} on each layer with
 *       row-objects.</li>
 *   <li>Translate declarative array-of-column-name accessors
 *       ({@code getPosition: ["lon","lat"]}) into actual function
 *       accessors deck.gl expects ({@code (d) => [d.lon, d.lat]}).</li>
 *   <li>Translate single-field numeric accessors
 *       ({@code getRadius: ["population"]}) into
 *       ({@code (d) => d.population}).</li>
 * </ol>
 *
 * <p>Mirrors the cognos-plugin's sibling so both UIs handle the same
 * backend spec shape identically. Data-driven color scaling
 * ({@code {field, scale}}) is left as pass-through — the backend's
 * prompt allows literal {@code [r,g,b]} arrays which deck.gl accepts
 * unchanged, so we don't need d3-scale on the client.
 */
export function prepareDeckGlSpec(payload: VizRenderPayload): {
  initialViewState: Record<string, unknown>;
  controller: unknown;
  layers: Array<Record<string, unknown>>;
  views?: Array<Record<string, unknown>>;
} {
  const spec = JSON.parse(payload.specJson) as {
    initialViewState: Record<string, unknown>;
    controller?: unknown;
    layers: Array<Record<string, unknown>>;
  };
  const objectRows = rowsAsObjects(payload.rows, payload.columnNames);

  const layers = spec.layers.map((layer) => {
    const out: Record<string, unknown> = { ...layer };

    // 1. Data substitution.
    if (out.data === "DATA_PLACEHOLDER") {
      out.data = objectRows;
    }

    // 2. Array-of-fields accessors: ["lon","lat"] → (d) => [d.lon, d.lat]
    for (const key of [
      "getPosition", "getSourcePosition", "getTargetPosition",
    ]) {
      const v = out[key];
      if (Array.isArray(v) && v.every((item) => typeof item === "string")) {
        const fields = v as string[];
        out[key] = (d: Record<string, unknown>) => fields.map((f) => d[f]);
      }
    }

    // 3. Single-field numeric accessors: ["sales"] → (d) => d.sales
    for (const key of [
      "getRadius", "getElevation", "getElevationWeight",
      "getColorWeight", "getWeight", "getLineWidth",
    ]) {
      const v = out[key];
      if (Array.isArray(v) && v.length === 1 && typeof v[0] === "string") {
        const field = v[0];
        out[key] = (d: Record<string, unknown>) => d[field];
      }
    }

    // 4. Single-field string accessor for TextLayer: ["label"] → (d) => String(d.label ?? "")
    //    Coerce to string because TextLayer requires textual values; a
    //    numeric or null cell would otherwise crash the layer.
    const txt = out["getText"];
    if (Array.isArray(txt) && txt.length === 1 && typeof txt[0] === "string") {
      const field = txt[0];
      out["getText"] = (d: Record<string, unknown>) => {
        const v = d[field];
        return v == null ? "" : String(v);
      };
    }

    return out;
  });

  return {
    initialViewState: spec.initialViewState,
    controller: spec.controller ?? true,
    layers,
    views: (spec as { views?: Array<Record<string, unknown>> }).views,
  };
}
